import {
  FormItemFilterRecord,
  ReportFilter,
  ReportRecordData,
  ReportWidget,
} from '@/services/api/resources/analytics-builder/model';
import { v4 as uuidv4 } from 'uuid';
import {
  allAttrsEnum,
  MetricsType,
  PunctualityType,
  ReportPreMadeTemplate,
} from '@/services/api/resources/analytics-builder/constants';
import { t } from '@/ui/i18n/i18n';
import { useAttributes } from '@/ui/concerns/analytics/widget';
import { ResponsesTypes } from '@/entities/checklist';

export const getDefaultFilterDate = () => ({
  range: null,
  dateStart: null,
  dateEnd: null,
});

export const getDefaultFilterValues = () => ({
  values: [],
});

export const getDefaultFilter = () => ({
  date: getDefaultFilterDate(),
  folder: getDefaultFilterValues(),
  entity: getDefaultFilterValues(),
  group: getDefaultFilterValues(),
  user: getDefaultFilterValues(),
  category: getDefaultFilterValues(),
  checklist: getDefaultFilterValues(),
  punctuality: null,
});

export const recreateFilter = (baseFilters: ReportFilter) => {
  const newFilters: ReportFilter = getDefaultFilter();

  if (baseFilters.section) {
    newFilters.section = getDefaultFilterValues();
  }
  if (baseFilters.priority) {
    newFilters.priority = null;
  }
  if (baseFilters.formStatus) {
    newFilters.formStatus = getDefaultFilterValues();
  }
  if (baseFilters.actionStatus) {
    newFilters.actionStatus = getDefaultFilterValues();
  }
  if (baseFilters.creator) {
    newFilters.creator = getDefaultFilterValues();
  }
  if (baseFilters.approvalStatus) {
    newFilters.approvalStatus = getDefaultFilterValues();
  }
  if (baseFilters.approvers) {
    newFilters.approvers = getDefaultFilterValues();
  }
  if (baseFilters.approvalDate) {
    newFilters.approvalDate = getDefaultFilterDate();
  }

  return newFilters;
};

const widgetMapper = (defaultMetric: MetricsType) =>
  (partial: Record<never, never>, index: number): ReportWidget => ({
    ...useReportWidget(index),
    metric: defaultMetric,
    ...partial,
  });

const getFormsTemplateReportWidgets = (): ReportWidget[] => [
  {
    attribute: allAttrsEnum.total,
    singleNumberDisplay: true,
  },
  {
    attribute: allAttrsEnum.punctuality,
    lineChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.entities,
    barChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.folders,
    barChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.users,
    barChartDisplay: true,
  },
  {
    metric: 'form-score' as MetricsType,
    attribute: allAttrsEnum.average,
    singleNumberDisplay: true,
    percentage: true,
  },
].map(widgetMapper('submitted-forms'));

const getActionsTemplateReportWidgets = (): ReportWidget[] => [
  {
    attribute: allAttrsEnum.total,
    singleNumberDisplay: true,
  },
  {
    attribute: allAttrsEnum['action-status'],
    lineChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.punctuality,
    lineChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.users,
    barChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.entities,
    barChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.folders,
    barChartDisplay: true,
  },
].map(widgetMapper('actions'));

const getNegativeResponsesTemplateReportWidgets = (): ReportWidget[] => [
  {
    attribute: allAttrsEnum.total,
    singleNumberDisplay: true,
  },
  {
    attribute: allAttrsEnum.year,
    barChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.templates,
    barChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.users,
    barChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.entities,
    lineChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.folders,
    lineChartDisplay: true,
  },
].map(widgetMapper('negative-responses'));

const getScoresTemplateReportWidgets = (): ReportWidget[] => [
  {
    attribute: allAttrsEnum.average,
    singleNumberDisplay: true,
    percentage: true,
  },
  // TODO: waiting for product to confirm average x line chart
  {
    attribute: allAttrsEnum.users,
    lineChartDisplay: true,
    percentage: true,
  },
  {
    attribute: allAttrsEnum.entities,
    lineChartDisplay: true,
    percentage: true,
  },
  {
    attribute: allAttrsEnum.folders,
    lineChartDisplay: true,
    percentage: true,
  },
  {
    attribute: allAttrsEnum.templates,
    lineChartDisplay: true,
    percentage: true,
  },
].map(widgetMapper('form-score'));

const getOverdueFormsTemplateReportWidgets = (): ReportWidget[] => [
  {
    attribute: allAttrsEnum.total,
    singleNumberDisplay: true,
  },
  // TODO: waiting for product to confirm total x line chart
  {
    attribute: allAttrsEnum.entities,
    barChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.folders,
    barChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.users,
    barChartDisplay: true,
  },
  {
    metric: 'form-score' as MetricsType,
    attribute: allAttrsEnum.average,
    singleNumberDisplay: true,
    percentage: true,
  },
].map(widgetMapper('submitted-forms'));

const getResultSummaryTemplateReportWidgets = (): ReportWidget[] => [
  {
    attribute: allAttrsEnum.total,
    singleNumberDisplay: true,
  },
  {
    attribute: allAttrsEnum.punctuality,
    lineChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.entities,
    lineChartDisplay: true,
  },
  {
    attribute: allAttrsEnum.users,
    lineChartDisplay: true,
  },
].map(widgetMapper('submitted-forms'));

export const useReportRecord = () => {
  const emptyReport = (): ReportRecordData => ({
    title: '',
    filters: getDefaultFilter(),
    createdAt: new Date(),
    updatedAt: new Date(),
    user: {
      id: 0,
      name: '',
    },
    widgets: [],
    schedule: null,
  });

  const createResultSummaryTemplateReport = (formId: number, formName: string, formItems: Array<FormItemFilterRecord>) => {
    const { getDisplayByResponseType } = useAttributes();
    const report = emptyReport();

    report.title = `${formName} - ${t('analytics.summary')}`;
    report.filters.checklist.values = [formId];
    report.widgets = getResultSummaryTemplateReportWidgets();

    let index = 0;
    formItems.forEach(item => {
      const display = getDisplayByResponseType(item.type as ResponsesTypes);

      if (!display) {
        return;
      }

      const widget = useReportWidget(index + report.widgets.length);
      widget.filters.formItem = item.id;
      report.widgets.push({
        ...widget,
        metric: 'form-items',
        attribute: allAttrsEnum.response,
        [display]: true,
      });

      index++;
    });

    return report;
  };

  const createTemplateReport = (type: ReportPreMadeTemplate): ReportRecordData => {
    const report = emptyReport();

    switch (type) {
      case ReportPreMadeTemplate.forms:
        report.widgets = getFormsTemplateReportWidgets();
        break;
      case ReportPreMadeTemplate.actions:
        report.widgets = getActionsTemplateReportWidgets();
        break;
      case ReportPreMadeTemplate.negativeResponses:
        report.widgets = getNegativeResponsesTemplateReportWidgets();
        break;
      case ReportPreMadeTemplate.scores:
        report.widgets = getScoresTemplateReportWidgets();
        break;
      case ReportPreMadeTemplate.overdueForms:
        report.widgets = getOverdueFormsTemplateReportWidgets();
        report.filters.punctuality = PunctualityType.tooLate;
        break;
      default:
        break;
    }

    return report;
  };

  return {
    emptyReport,
    createTemplateReport,
    createResultSummaryTemplateReport,
  };
};

export const useReportWidget = (order: number): ReportWidget => ({
  id: 0,
  key: uuidv4(),
  order,
  metric: null,
  attribute: null,
  filters: getDefaultFilter(),
  percentage: false,
  singleNumberDisplay: false,
  barChartDisplay: false,
  lineChartDisplay: false,
  pieChartDisplay: false,
  tableDisplay: false,
  lineChartTimeGrouping: null,
});
